import { Check, ArrowRight } from 'lucide-react';

const plans = [
  {
    name: 'Starter',
    price: '$499',
    period: '/month',
    description: 'Perfect for small trading operations',
    features: [
      'Up to 3 trading accounts',
      '10 contracts position tracking',
      'Daily position imports',
      'Email alerts',
      'Basic reporting',
      'Email support',
    ],
    cta: 'Get Started',
    popular: false,
  },
  {
    name: 'Professional',
    price: '$1,499',
    period: '/month',
    description: 'For growing trading firms',
    features: [
      'Up to 15 trading accounts',
      'Unlimited contracts',
      'Real-time position updates',
      'SMS & email alerts',
      'Advanced analytics',
      'Audit trail & reporting',
      'API access',
      'Priority support',
    ],
    cta: 'Get Started',
    popular: true,
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    period: '',
    description: 'For large institutions',
    features: [
      'Unlimited accounts',
      'Unlimited contracts',
      'Real-time streaming data',
      'Custom alert rules',
      'White-label options',
      'On-premise deployment',
      'Dedicated support team',
      'Custom integrations',
      'SLA guarantee',
    ],
    cta: 'Contact Sales',
    popular: false,
  },
];

export function PricingSection() {
  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="pricing" className="relative py-20 md:py-28 bg-white">
      <div className="container relative">
        {/* Section Header */}
        <div className="mx-auto max-w-2xl text-center mb-12">
          <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 border border-slate-200 px-4 py-1.5 text-sm mb-6">
            <span className="text-slate-700 font-medium">Pricing</span>
          </div>
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4 text-slate-900">
            Simple,{' '}
            <span className="text-purple-600">
              Transparent
            </span>{' '}
            Pricing
          </h2>
          <p className="text-lg text-slate-600">
            Choose the plan that fits your trading operation. All plans include core compliance features.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid gap-8 md:grid-cols-3 max-w-5xl mx-auto items-start">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`relative rounded-2xl ${
                plan.popular
                  ? 'md:scale-105 z-10'
                  : ''
              }`}
            >
              {/* Border for popular plan */}
              {plan.popular && (
                <div className="absolute -inset-[2px] bg-purple-600 rounded-2xl" />
              )}

              <div className={`relative rounded-2xl bg-white ${
                plan.popular ? 'shadow-xl' : 'border border-slate-200 shadow-sm'
              } p-6 md:p-8 h-full`}>
                {/* Popular Badge */}
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-purple-600 px-4 py-1.5 text-xs font-semibold text-white">
                      Most Popular
                    </span>
                  </div>
                )}

                {/* Plan Header */}
                <div className="text-center mb-6 pt-2">
                  <h3 className="text-lg font-semibold mb-2 text-slate-900">{plan.name}</h3>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl font-bold text-slate-900">
                      {plan.price}
                    </span>
                    <span className="text-slate-500">{plan.period}</span>
                  </div>
                  <p className="mt-2 text-sm text-slate-500">
                    {plan.description}
                  </p>
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2">
                      <div className="p-0.5 rounded-full bg-purple-600 mt-0.5">
                        <Check className="h-3 w-3 text-white" />
                      </div>
                      <span className="text-sm text-slate-600">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <a
                  href="#demo"
                  onClick={(e) => scrollToSection(e, '#demo')}
                  className={`flex items-center justify-center gap-2 w-full rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${
                    plan.popular
                      ? 'bg-purple-600 text-white hover:bg-purple-700'
                      : 'border-2 border-slate-200 bg-white text-slate-700 hover:border-purple-300 hover:bg-purple-50'
                  }`}
                >
                  {plan.cta}
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* FAQ Link */}
        <p className="mt-12 text-center text-slate-500">
          Have questions about pricing?{' '}
          <a
            href="#faq"
            onClick={(e) => scrollToSection(e, '#faq')}
            className="text-purple-600 font-medium hover:underline"
          >
            Check our FAQ
          </a>{' '}
          or{' '}
          <a
            href="#demo"
            onClick={(e) => scrollToSection(e, '#demo')}
            className="text-purple-600 font-medium hover:underline"
          >
            contact us
          </a>
          .
        </p>
      </div>
    </section>
  );
}
